'use client';

import type { CiCFeature, DoorStyleFinish } from '@dealerportal/common';
import { logger } from '@dealerportal/core';
import { useEffect, useRef, useState } from 'react';
import { fetchCiCFeatures } from '../../Mappers/cicFeatures';
import {
  DOOR_FEATURE_CODE,
  FINISH_FEATURE_CODE,
} from '../Configurator/featureHelpers';
import { hasCiCAPI } from '../Configurator/useItemConfiguration';

/** CiC's conventional code for the wood species style feature. */
export const WOOD_FEATURE_CODE = 'g_wood';

/** CiC base item type of cabinets — the default thing to show a door style on. */
export const CABINET_BASE_ITEM_TYPE = 'cabinetry.cabinet';

/**
 * CiC characteristics of a standard double-door base cabinet: two doors and
 * a drawer front show a door style and finish far better than a 12" single
 * door.
 */
export const DOUBLE_DOOR_BASE_CHARACTERISTICS = {
  subType: ['base'],
  cabinetType: ['standard'],
  openingDirection: ['both'],
};

/** Width of cabinet to show door styles on. */
const DISPLAY_WIDTH_INCHES = 36;
const MM_PER_INCH = 25.4;
/** No cabinet is this many inches wide; a larger width is in millimetres. */
const MAX_INCH_WIDTH = 120;
/** How many double-door bases to choose a width from. */
const DISPLAY_CANDIDATES = 50;

export type DisplayItem = {
  /** CiC item id, e.g. `92784.b12l`. */
  id: string;
  code: string;
  name: string;
};

export type DisplayResolution = {
  /** Door style code CiC actually rendered. */
  door: string;
  /** Finish code CiC actually rendered. */
  finish: string;
  /** Resolved configuration state on the display item. */
  state: IFeatureState[];
  /** False when CiC substituted another door or finish on the display item. */
  exact: boolean;
};

export type DisplayItemState = {
  item?: DisplayItem;
  resolution?: DisplayResolution;
  /** The display item's features with the door and finish applied. */
  features: CiCFeature[];
  isLoading: boolean;
};

export type DisplayItemOptions = {
  /** Catalog version the door style belongs to, e.g. `'92784'`. */
  catalogVersionId: string;
  /**
   * Item to render the door style on; unset, or not in this catalog → a
   * double-door base cabinet about 36" wide (see `findDisplayItem`).
   */
  displayItemCode?: string;
  doorCode: string;
  finish: Pick<DoorStyleFinish, 'code' | 'species'>;
  /** Only run once the CiC SDK has initialised. */
  enabled?: boolean;
};

const codeOf = (id: string | undefined): string | undefined =>
  id?.split('.').slice(1).join('.');

type ListedItem = {
  id: string;
  code: string;
  names?: { main?: string };
  dimensions?: { width?: number };
};

const toDisplayItem = (item: ListedItem): DisplayItem => ({
  id: item.id,
  code: item.code,
  name: item.names?.main ?? '',
});

/**
 * The item whose width is nearest a 36" cabinet, in catalog order on a tie;
 * widths are in the catalog's unit (inches, or millimetres). The first item
 * when none has a width. Exported for tests.
 */
export const closestToDisplayWidth = <T extends ListedItem>(
  items: T[]
): T | undefined => {
  let best: T | undefined;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const item of items) {
    const width = item.dimensions?.width;
    if (typeof width !== 'number' || !(width > 0)) continue;
    const inches = width > MAX_INCH_WIDTH ? width / MM_PER_INCH : width;
    const distance = Math.abs(inches - DISPLAY_WIDTH_INCHES);
    if (distance < bestDistance) {
      best = item;
      bestDistance = distance;
    }
  }
  return best ?? items[0];
};

/**
 * The item to render on: the configured one when this catalog has it, else
 * the catalog's double-door base cabinet nearest 36" wide, else its first
 * visible cabinet (CiC returns search results in the manufacturer's own
 * order). Exported for tests.
 */
export const findDisplayItem = async (
  catalogVersionId: string,
  displayItemCode?: string
): Promise<DisplayItem | null> => {
  const v2 = globalThis.CiCAPI.content.v2;
  if (displayItemCode) {
    const { result } = await v2.getItemVariant({
      itemId: `${catalogVersionId}.${displayItemCode.toLowerCase()}`,
    });
    // A dealer's catalogs differ; the setting may name another one's item.
    if (result) return toDisplayItem(result);
  }
  const searchCabinets = async (
    nbPerPage: number,
    characteristics?: Record<string, string[]>
  ): Promise<ListedItem[]> => {
    const { result } = await v2.searchItems('', {
      catalogVersionIds: [catalogVersionId],
      classificationFilter: {
        baseItemTypes: [CABINET_BASE_ITEM_TYPE],
        ...(characteristics ? { characteristics } : {}),
      },
      nbPerPage,
      offset: 0,
      visibleOnly: true,
      useFilterConfig: false,
    });
    return result?.items ?? [];
  };
  const wide = closestToDisplayWidth(
    await searchCabinets(DISPLAY_CANDIDATES, DOUBLE_DOOR_BASE_CHARACTERISTICS)
  );
  if (wide) return toDisplayItem(wide);
  const [first] = await searchCabinets(1);
  return first ? toDisplayItem(first) : null;
};

/**
 * Have CiC resolve the display item's configuration for a door and finish.
 *
 * A finish is only orderable under its wood species, and CiC silently swaps
 * an invalid pick for the item's default, so the species is set explicitly:
 * the finish's own species first, then every other species the item offers,
 * until CiC hands back exactly the door and finish asked for. If none does,
 * the first answer stands, flagged `exact: false`. Exported for tests.
 */
export const resolveDisplayConfiguration = async (
  itemId: string,
  doorCode: string,
  finish: Pick<DoorStyleFinish, 'code' | 'species'>
): Promise<DisplayResolution | null> => {
  const v2 = globalThis.CiCAPI.content.v2;
  const { result } = await v2.getItemFeatures([{ itemId }]);
  const features = result?.features ?? [];
  const byCode = (code: string) => features.find((f) => f.code === code);
  const doorFeature = byCode(DOOR_FEATURE_CODE);
  const finishFeature = byCode(FINISH_FEATURE_CODE);
  const woodFeature = byCode(WOOD_FEATURE_CODE);
  if (!doorFeature || !finishFeature) return null;

  const catalogPrefix = itemId.split('.')[0];
  const state = (
    feature: { id: string; code: string },
    optionCode: string
  ): IFeatureState => ({
    featureId: feature.id,
    optionId: `${catalogPrefix}.${optionCode}`,
    featureRefCodes: { manufCode: feature.code },
    optionRefCodes: { manufCode: optionCode },
    source: 'user',
  });

  let species: string[] = [];
  if (woodFeature) {
    const options = await v2.getItemFeatureOptions(
      [{ itemId }],
      woodFeature.id
    );
    const wanted = finish.species.toLowerCase();
    const matches = (o: { code: string; name: string }) =>
      o.code.toLowerCase() === wanted || o.name.toLowerCase() === wanted;
    const all = options.result?.options ?? [];
    species = [
      ...all.filter(matches).map((o) => o.code),
      ...all.filter((o) => !matches(o)).map((o) => o.code),
    ];
  }

  let fallback: DisplayResolution | null = null;
  for (const wood of species.length > 0 ? species : [undefined]) {
    const picks = [
      state(doorFeature, doorCode),
      state(finishFeature, finish.code),
      ...(wood && woodFeature ? [state(woodFeature, wood)] : []),
    ];
    // biome-ignore lint/performance/noAwaitInLoops: tries species in priority order; stops at the first exact match
    const variant = await v2.getItemVariant({
      itemId,
      configurationState: picks,
    });
    const resolved = variant.result?.configurationState ?? picks;
    const optionFor = (featureId: string) =>
      codeOf(resolved.find((s) => s.featureId === featureId)?.optionId);
    const door = optionFor(doorFeature.id) ?? doorCode;
    const rendered = optionFor(finishFeature.id) ?? finish.code;
    const candidate: DisplayResolution = {
      door,
      finish: rendered,
      state: resolved,
      exact: door === doorCode && rendered === finish.code,
    };
    if (candidate.exact) return candidate;
    fallback ??= candidate;
  }
  return fallback;
};

/**
 * The catalog item a door style is shown on, configured in that door style
 * and finish: which item (site setting, else the catalog's first cabinet),
 * how CiC resolves the pair on it, and the item's features under that
 * resolution — everything the configurator and 3D view need. Nothing about
 * the catalog is assumed here; it is all asked of the SDK.
 */
export const useDisplayItem = ({
  catalogVersionId,
  displayItemCode,
  doorCode,
  finish,
  enabled = true,
}: DisplayItemOptions): DisplayItemState => {
  const [display, setDisplay] = useState<DisplayItemState>({
    features: [],
    isLoading: false,
  });

  // Keyed on the primitive values, not the finish object, so a re-render
  // with an equivalent finish does not refetch.
  const finishCode = finish.code;
  const finishSpecies = finish.species;
  const requestSeq = useRef(0);

  useEffect(() => {
    if (!enabled || !hasCiCAPI()) {
      setDisplay({ features: [], isLoading: false });
      return;
    }
    const seq = ++requestSeq.current;
    let cancelled = false;
    setDisplay((previous) => ({ ...previous, isLoading: true }));

    (async () => {
      const item = await findDisplayItem(catalogVersionId, displayItemCode);
      if (!item) throw new Error('The catalog has no item to display on');
      const resolution = await resolveDisplayConfiguration(item.id, doorCode, {
        code: finishCode,
        species: finishSpecies,
      });
      if (!resolution) {
        throw new Error(`${item.id} has no door style or finish feature`);
      }
      const features = await fetchCiCFeatures(
        [{ itemId: item.id, configurationState: resolution.state }],
        { onlyVisibleSelectable: true }
      );
      if (cancelled || seq !== requestSeq.current) return;
      setDisplay({ item, resolution, features, isLoading: false });
    })().catch((error) => {
      if (cancelled || seq !== requestSeq.current) return;
      logger
        .withError(error)
        .warn(`Could not show ${doorCode} in ${finishCode} on a display item`);
      setDisplay({ features: [], isLoading: false });
    });

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    catalogVersionId,
    displayItemCode,
    doorCode,
    finishCode,
    finishSpecies,
  ]);

  return display;
};
